import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import { useGlobalContext } from "../../context/stateContext";
import {
  Package,
  Building2,
  Search,
  ArrowRight,
  MapPin,
  Pill,
  FileText,
  TrendingUp,
  Clock,
  AlertCircle,
  Upload,
} from "lucide-react";
import BulkBatchUploadModal from "./BulkBatchUploadModal";
import RoleBasedComponent from "../../components/RoleBasedComponent";
import { useNavigate } from "react-router-dom";

const BatchTracking = () => {
  const navigate = useNavigate();
  const searchRef = useRef();
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();
  const [loading, setLoading] = useState(false);
  const [loadingDrugs, setLoadingDrugs] = useState(false);
  const [drugsDropdown, setDrugsDropdown] = useState(true);
  const [drugs, setDrugs] = useState([]);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const listContainerRef = useRef(null);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("");
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const branchSelectRef = useRef(null);

  const getDrugs = async (search_query) => {
    setLoadingDrugs(true);
    try {
      const data = await requestWithAuth(
        `/inventory/company/?paginated=false&search=${search_query}`,
        "GET"
      );
      setDrugs(data);
    } catch (error) {
      console.error("Error fetching drugs data:", error);
    } finally {
      setLoadingDrugs(false);
    }
  };



  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?pageSize=100`,
        "GET"
      );
      setBranches(data?.results);
    } catch (error) {
      console.error("Error fetching users data:", error);
    }
  };

  const getCreditSales = async () => {
    if (!selectedDrug || !branch) {
      toast.error("Please select both drug and branch");
      return;
    }
    navigate(`/inventory/batch-tracking/print?branch=${branch}&drug=${selectedDrug?.id}`);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    if (debouncedSearch.length >= 3) {
      getDrugs(debouncedSearch);
    } else {
      setDrugs([]);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (drugsDropdown) {
      setTimeout(() => {
        searchRef.current?.focus();
      }, 0);
    } else {
      setSelectedResultIndex(-1);
    }
  }, [drugsDropdown]);

  const handleSearchKeyDown = (e) => {
    if (!drugs.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedResultIndex((prev) =>
        prev < drugs.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedResultIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedResultIndex >= 0 && selectedResultIndex < drugs.length) {
        setSelectedDrug(drugs[selectedResultIndex]);
        setDrugsDropdown(false);
        setSearch("");
        setTimeout(() => {
          branchSelectRef.current?.focus();
        }, 100);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDrugsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (selectedResultIndex >= 0 && listContainerRef.current) {
      const selectedElement = listContainerRef.current.querySelector(
        `#drug-result-${selectedResultIndex}`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedResultIndex]);

  useEffect(() => {
    fetchBranches();
  }, []);



  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Batch Tracking
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Package size={32} className="text-blue-600" />
                Batch Tracking
              </h1>
            </div>
            <RoleBasedComponent allowedRoles={["superadmin", "admin"]}>
              <button
                onClick={() => setIsBulkUploadModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 font-medium transition-colors shadow-sm flex items-center gap-2"
              >
                <Upload size={18} />
                Bulk batch upload
              </button>
            </RoleBasedComponent>
          </div>
        </div>

        {/* Main Content - Centered Card */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Card Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="text-blue-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Track Drug Batches
                  </h2>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Monitor batch inventory across branches
                  </p>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6">
              <div className="space-y-6">
                {/* Drug Selection */}
                <div>
                  <label
                    htmlFor="drug-select"
                    className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2"
                  >
                    <Pill size={16} className="text-gray-500" />
                    Drug Name
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="relative" ref={dropdownRef}>
                    <div
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent bg-white transition-all cursor-pointer hover:border-gray-400"

                      onClick={() => {
                        setDrugsDropdown(!drugsDropdown);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Search size={16} className="text-gray-400" />
                        <span
                          className={
                            selectedDrug ? "text-gray-900" : "text-gray-400"
                          }
                        >
                          {selectedDrug?.name || "Search and select a drug..."}
                        </span>
                      </div>
                    </div>

                    {/* Dropdown */}
                    {drugsDropdown && (
                      <div
                        className="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-3 border-b border-gray-200">
                          <div className="relative">
                            <Search
                              size={16}
                              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                            />
                            <input
                              ref={searchRef}
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              type="text"
                              onKeyDown={handleSearchKeyDown}
                              placeholder="Type to search drugs..."
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>

                        <div className="max-h-64 overflow-y-auto" ref={listContainerRef}>
                          {debouncedSearch?.length < 3 ? (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              <AlertCircle
                                size={20}
                                className="mx-auto mb-2 text-gray-400"
                              />
                              Please enter at least 3 characters
                            </div>
                          ) : loadingDrugs ? (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                              Searching...
                            </div>
                          ) : drugs?.length > 0 ? (
                            drugs?.map((drug_item, index) => (
                              <div
                                key={index}
                                onClick={() => {
                                  setSelectedDrug(drug_item);
                                  setDrugsDropdown(false);
                                  setSearch("");
                                  setTimeout(() => {
                                    branchSelectRef.current?.focus();
                                  }, 100);
                                }}
                                id={`drug-result-${index}`}
                                className={`p-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${selectedResultIndex === index
                                  ? "bg-blue-300"
                                  : "hover:bg-blue-50"
                                  }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-gray-900">
                                    {drug_item?.name}
                                  </span>
                                  <span className="text-sm text-blue-600 font-semibold">
                                    GH₵ {drug_item?.cash_price}
                                  </span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              <AlertCircle
                                size={20}
                                className="mx-auto mb-2 text-gray-400"
                              />
                              No results found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Search by drug name to track its batches
                  </p>
                </div>

                {/* Branch Selection */}
                <div>
                  <label
                    htmlFor="branch-select"
                    className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2"
                  >
                    <MapPin size={16} className="text-gray-500" />
                    Branch Name
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="branch-select"
                    ref={branchSelectRef}
                    onChange={(e) => setBranch(e.target.value)}
                    value={branch}
                    required
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
                  >
                    <option value="">Select a branch...</option>
                    {branches?.map((branch, index) => (
                      <option key={index} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    Select the branch to view batch information
                  </p>
                </div>

                {/* Info Card */}
                {selectedDrug && branch && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <FileText size={18} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-blue-900">
                          Ready to track
                        </p>
                        <p className="text-xs text-blue-700 mt-1">
                          View batch tracking report for{" "}
                          <span className="font-semibold">
                            {selectedDrug.name}
                          </span>{" "}
                          at selected branch
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Card Footer */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex justify-end">
                  {loading ? (
                    <button
                      type="button"
                      disabled
                      className="bg-blue-400 text-white rounded-lg px-6 py-3 flex items-center gap-2 cursor-not-allowed"
                    >
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Loading...
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={getCreditSales}
                      disabled={!selectedDrug || !branch}
                      className={`rounded-lg px-6 py-3 font-medium transition-colors shadow-sm flex items-center gap-2 ${selectedDrug && branch
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                    >
                      View Batch Report
                      <ArrowRight size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              What is Batch Tracking?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Package size={16} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Monitor Batches
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Track drug batches and their quantities
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock size={16} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Expiry Dates
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    View batch expiration information
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Building2 size={16} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Branch Level
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Track inventory by specific location
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp size={16} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Stock Movement
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Analyze batch usage patterns
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Batch Upload Modal */}
      <BulkBatchUploadModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
      />
    </div>
  );
};

export default BatchTracking;
